import MojitoCore from 'mojito/core';
import MojitoServices from 'mojito/services';
import MojitoPresentation from 'mojito/presentation';
import { getParticipantName, resolveMarketLayout } from 'modules/market/helper/market-helper';
import Market from 'modules/market/index.js';
import PromotionUtils from 'modules/promotions/utils/promotion-utils';
import { connect } from 'react-redux';

const { UIViewImplementation } = MojitoCore.Presentation;
const { DateTime } = MojitoPresentation.Components;
const { utils: EventUtils, descriptors: EventDescriptor } = MojitoServices.SportsContent.Events;
const { createEventDescriptor, createEventMarketDescriptor } = EventDescriptor;
const { flatMerge } = MojitoCore.Base.objUtils;
const RequestDataHelper = MojitoCore.Services.RequestDataHelper;
const { selectEventItem, makeSelectMarketsByIds } = MojitoServices.SportsContent.Events.selectors;
const { getSeparatorTranslationKey } = MojitoPresentation.Utils.ParticipantSeparator;

class BannerMarket extends UIViewImplementation {
    constructor(props) {
        super(props);

        this.requestDataHelper = new RequestDataHelper(this.instanceId);
    }

    componentDidMount() {
        this.requestData();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.shouldRequestData !== this.props.shouldRequestData) {
            this.requestData();
        }
    }

    requestData() {
        const { shouldRequestData, event, marketId } = this.props;
        if (shouldRequestData) {
            this.requestDataHelper.requestData(createEventDescriptor(event.id));
            this.requestDataHelper.requestData(createEventMarketDescriptor(marketId));
        }
    }

    componentWillUnmount() {
        this.requestDataHelper.unsubscribeAll();
    }

    renderTimeOrStatus() {
        const event = this.props.eventItem;
        if (!event) {
            return null;
        }
        if (event.details.inPlay) {
            return (
                <div style={this.style.startTime}>
                    {this.resolveString('$BANNERMARKET.HEADER.LIVE')}
                </div>
            );
        }

        return (
            <div style={this.style.startTime}>
                <DateTime config={this.config.startTimeFormat} dateTime={event.startTime} />
            </div>
        );
    }

    renderSportLeague() {
        const sportName = this.appContext().sportName(this.props.eventItem.sportId);
        const label = [sportName, this.config.sportLeague.delimiter, this.props.eventItem.typeName]
            .filter(Boolean)
            .join('');
        return <div style={this.style.sportLeague}>{label}</div>;
    }

    resolveMarket() {
        const { market, event } = this.props;

        const outrightSelections = PromotionUtils.getTopSelections(
            market,
            this.config.maxSelectionsAmount
        );

        return EventUtils.isOutrightEventType(event.eventType)
            ? { ...market, selections: outrightSelections }
            : market;
    }

    renderSelections() {
        const { market, eventItem } = this.props;

        if (!market?.selections) {
            return null;
        }
        const marketLayout = resolveMarketLayout(
            market,
            this.config.layoutDescriptors,
            eventItem.sportId
        );
        // This is needed to keep selections of specific types in corresponding columns like [1, X, 2], [O, U], [1, 2] etc.
        const selectionTypes = marketLayout.selectionTypes || marketLayout.default.selectionTypes;

        return (
            <Market
                market={this.resolveMarket()}
                event={eventItem}
                config={this.config.market}
                selectionTypes={selectionTypes}
            />
        );
    }

    renderParticipantRow(participantName) {
        return <div style={this.style.eventName}>{participantName}</div>;
    }

    renderLabel() {
        const { market, eventItem } = this.props;

        if (!eventItem) {
            return null;
        }

        const { participants, name, homeAway } = eventItem;

        if (participants && participants.length === 2) {
            const showSeparator =
                market?.selections?.length === 2 ? this.config.showEventNameSeparator : true;

            return (
                <div style={this.style.participants}>
                    {this.renderParticipantRow(getParticipantName(eventItem, 0))}
                    {showSeparator && (
                        <div style={this.style.eventName}>
                            {this.resolveString(getSeparatorTranslationKey(homeAway))}
                        </div>
                    )}
                    {this.renderParticipantRow(getParticipantName(eventItem, 1))}
                </div>
            );
        }

        return <div style={this.style.eventName}>{name}</div>;
    }

    render() {
        if (!this.props.eventItem) {
            return null;
        }
        return (
            <div style={this.style.container} className={`oddsbanner-${this.props.marketId}`}>
                {this.renderSportLeague()}
                {this.renderTimeOrStatus()}
                {this.renderLabel()}
                {this.renderSelections()}
            </div>
        );
    }
}

BannerMarket.getStyle = function (config, applicationMode, merge) {
    const textOverflowEllipsis = {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    };
    return {
        container: merge(
            {
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            },
            config.style
        ),
        eventName: merge(textOverflowEllipsis, {
            width: '100%',
            textAlign: 'center',
            color: config.eventName.text.color,
            fontSize: config.eventName.text.fontSize,
            fontFamily: config.eventName.text.fontFamily,
            fontWeight: config.eventName.text.fontWeight,
            textTransform: config.eventName.text.textTransform,
            margin: config.eventName.margin,
        }),
        participants: merge(config.participants, {
            display: 'flex',
        }),
        sportLeague: flatMerge(config.sportLeague, {
            width: '100%',
        }),
        startTime: flatMerge(config.startTime, {
            position: 'absolute',
        }),
    };
};

const mapStateToProps = (state, ownProps) => {
    const selectMarkets = makeSelectMarketsByIds();
    return {
        eventItem: selectEventItem(ownProps.event.id, state),
        market: selectMarkets([ownProps.event.market.marketId])[0],
    };
};

export default connect(mapStateToProps)(BannerMarket);
